import * as React from "react";
import { cloneElement, useEffect, useState } from "react";
import {
  TopToolbar,
  FilterButton,
  ExportButton,
  ReferenceInput,
  SelectInput,
  usePermissions,
} from "react-admin";

// Managers get a read-only, state-scoped view of users, offers, negotiations,
// invoices and orders (the backend filters the data and rejects writes).
// These helpers hide the write actions so managers aren't shown buttons that
// would only fail.
export const useIsManager = () => {
  const { permissions } = usePermissions();
  return permissions === "manager";
};

// List toolbar without the Create button (keeps filters and export).
// Lists that pass `filters` as a <Filter> element (e.g. Offers) get it here as
// a prop and must render it in button mode; array-style filters are read by
// FilterButton from the FilterContext instead (same as react-admin's ListActions).
export const ReadOnlyListActions = ({ filters }) => (
  <TopToolbar>
    {filters ? cloneElement(filters, { context: "button" }) : <FilterButton />}
    <ExportButton />
  </TopToolbar>
);

// State filter dropdown: managers only get their assigned states, fetched
// fresh because an admin can change assignments while they're logged in;
// everyone else gets the full state list as before.
export const StateFilterInput = ({ source, label = "State", ...rest }) => {
  const isManager = useIsManager();
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (!isManager) return;
    const fetchAssignedStates = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const response = await fetch(
          `${process.env.REACT_APP_URL}/admin-state-assignments/me`,
          {
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
        const statesData = (await response.json()) || [];
        localStorage.setItem("states", JSON.stringify(statesData));
        setStates(statesData);
      } catch (err) {
        console.error("Error fetching assigned states:", err);
      }
    };
    fetchAssignedStates();
  }, [isManager]);

  if (!isManager) {
    return (
      <ReferenceInput
        label={label}
        source={source}
        reference="state"
        allowEmpty
        {...rest}
      >
        <SelectInput optionText="name" />
      </ReferenceInput>
    );
  }

  return (
    <SelectInput
      label={label}
      source={source}
      choices={states}
      optionText="name"
      optionValue="id"
      allowEmpty
      {...rest}
    />
  );
};
